import type { FormPermissionsStructure } from '../model/form-permissions.types';
import type { FormRulesStructure } from '../model/form-rules.types';
import type { FormElement, FormStructure, TableElement } from '../model/form.types';
import type {
  AnswersBody,
  CompileOptions,
  FieldValue,
  FillSubject,
  MatrixAnswers,
  TableAnswers,
  ValidationIssue,
} from '../model/types';
import { isEmptyValue } from './definitions/shared';
import {
  fieldEntries,
  hasStoredValue,
  type FieldEntry,
  isTableElement,
  migratedElements,
  sameAnswer,
  walkElements,
} from './field-walk';
import { TABLE_NEW_ROWS_LIMIT } from './limits';
import { modelRowFieldByColumn } from './model-row';
import { canonicalValue } from './normalize-answers';
import { resolveFieldAccess, type FieldAccessResolver } from './permissions';
import { ruleLockedIds, tableColumns, type TableColumns } from './rule-locks';

export const FROZEN_MESSAGE = 'Campo congelado: não pode ser alterado nesta resposta.';
export const FORBIDDEN_MESSAGE = 'Sem permissão para alterar este campo.';
export const TABLE_APPEND_FORBIDDEN_MESSAGE = 'Você não pode acrescentar linhas nesta tabela.';
export const TABLE_NEW_ROWS_LIMIT_MESSAGE = `Cada resposta acrescenta no máximo ${TABLE_NEW_ROWS_LIMIT} linhas nesta tabela.`;
export const TABLE_EDITED_ROW_MISSING_MESSAGE = 'A linha corrigida não existe nesta tabela.';

export interface AcceptAnswersInput {
  structure: FormStructure;
  permissions: FormPermissionsStructure | null;
  rules: FormRulesStructure | null;
  subject: FillSubject;
  options?: CompileOptions;
  previous: MatrixAnswers | null;
  incoming: MatrixAnswers;
}

export interface AcceptedAnswers {
  answers: MatrixAnswers;
  issues: ValidationIssue[];
}

function storedValue(values: Record<string, FieldValue>, fieldId: string): FieldValue | undefined {
  return hasStoredValue(values, fieldId) ? values[fieldId] : undefined;
}

// The value the sheet showed: a field never answered shows the builder default, so comparing with
// the stored value alone would refuse the echo of that default.
function shownValue({ element, definition }: FieldEntry, values: Record<string, FieldValue>): FieldValue {
  return hasStoredValue(values, element.id) ? values[element.id] : definition.emptyValue(element);
}

// The body went through normalizeAnswers and the stored photo did not, so a value saved before a
// coercion existed (a URL without the scheme) would never equal its own echo.
function canonicalBase({ element, definition }: FieldEntry, value: FieldValue | undefined): FieldValue | undefined {
  return value === undefined ? value : canonicalValue(definition.control(element), value);
}

interface AcceptedTables {
  tables: Record<string, TableAnswers>;
  issues: ValidationIssue[];
}

type ExtraRow = Record<number, FieldValue>;

function appendedRow(row: ExtraRow, editableColumns: number[]): ExtraRow {
  const accepted: ExtraRow = {};
  for (const columnIndex of editableColumns) {
    if (Object.prototype.hasOwnProperty.call(row, columnIndex)) accepted[columnIndex] = row[columnIndex];
  }
  return accepted;
}

// An added row starts blank, never with the builder default, so any value in a column the subject
// cannot edit is a change.
function forbiddenCells(
  row: ExtraRow,
  rowIndex: number,
  table: TableElement,
  columns: Map<number, { element: FormElement }>,
  editableColumns: number[],
): ValidationIssue[] {
  return [...columns.entries()]
    .filter(([columnIndex]) => !editableColumns.includes(columnIndex) && !isEmptyValue(row[columnIndex] ?? null))
    .map(([columnIndex, column]) => ({
      fieldId: column.element.id,
      code: 'forbidden' as const,
      message: FORBIDDEN_MESSAGE,
      cell: { tableId: table.id, rowIndex, columnIndex },
    }));
}

// Both empty is no edit, the rule listWrittenFieldIds applies to fields: a body that sends '' for
// a cell the row was saved without must not store an edit nobody made.
function correctedRow(stored: ExtraRow, incoming: ExtraRow | undefined, editableColumns: number[]): ExtraRow {
  const corrected: ExtraRow = { ...stored };
  if (!incoming) return corrected;
  for (const columnIndex of editableColumns) {
    if (!Object.prototype.hasOwnProperty.call(incoming, columnIndex)) continue;
    const value = incoming[columnIndex];
    if (sameAnswer(stored[columnIndex], value)) continue;
    corrected[columnIndex] = value;
  }
  return corrected;
}

// A reopened row refuses only a column the subject cannot edit; a row that is not reopened refuses
// any change. As on a field, a missing permission is reported before the freeze.
function refusedHistoricalCells(
  stored: ExtraRow,
  incoming: ExtraRow | undefined,
  rowIndex: number,
  table: TableElement,
  columns: Map<number, { element: FormElement }>,
  lock: { reopened: boolean; editable: number[] },
): ValidationIssue[] {
  if (!incoming) return [];
  return Object.keys(incoming)
    .map(Number)
    .filter((columnIndex) => columns.has(columnIndex) && !sameAnswer(stored[columnIndex], incoming[columnIndex]))
    .filter((columnIndex) => !lock.reopened || !lock.editable.includes(columnIndex))
    .map((columnIndex) => {
      const forbidden = !lock.editable.includes(columnIndex);
      return {
        fieldId: columns.get(columnIndex)!.element.id,
        code: forbidden ? ('forbidden' as const) : ('frozen' as const),
        message: forbidden ? FORBIDDEN_MESSAGE : FROZEN_MESSAGE,
        cell: { tableId: table.id, rowIndex, columnIndex },
      };
    });
}

function multipleTables(structure: FormStructure): TableElement[] {
  const byId = new Map<string, TableElement>();
  for (const { element } of walkElements(migratedElements(structure))) {
    if (isTableElement(element) && element.properties.is_multiple === true && !byId.has(element.id)) {
      byId.set(element.id, element);
    }
  }
  return [...byId.values()];
}

interface AcceptedRows {
  rows: ExtraRow[];
  issues: ValidationIssue[];
}

// With no editable column an added row could only ever be empty, and once stored it turns
// historical and frozen for good: nobody could fill it or take it out. With editable columns that
// the rules all lock, the sheet drops the row too, so it goes away silently.
function appendedRows(
  table: TableElement,
  appended: ExtraRow[],
  firstRowIndex: number,
  columns: TableColumns,
): AcceptedRows {
  if (columns.editable.length === 0) {
    return {
      rows: [],
      issues: appended.map((_, offset) => ({
        fieldId: table.id,
        code: 'type' as const,
        message: TABLE_APPEND_FORBIDDEN_MESSAGE,
        cell: { tableId: table.id, rowIndex: firstRowIndex + offset },
      })),
    };
  }
  const modelColumns = modelRowFieldByColumn(table);
  return {
    rows: columns.writable.length > 0 ? appended.map((row) => appendedRow(row, columns.writable)) : [],
    issues: appended.flatMap((row, offset) =>
      forbiddenCells(row, firstRowIndex + offset, table, modelColumns, columns.editable),
    ),
  };
}

// Historical rows are copied verbatim from the previous photo, never from the body. The incoming
// rows went through withCorrectedRows, so a historical row is the sparse row of the cells the body
// edits and only the ones past the history are new. A reopened row (every row in a correction, the
// subject's own rows otherwise) takes the cells the subject may write; a column the rules lock keeps
// its stored cell silently, and any other edit is refused, so nobody believes it was saved.
function acceptTable(
  input: AcceptAnswersInput,
  table: TableElement,
  access: FieldAccessResolver,
  ruleLocked: ReadonlySet<string>,
): AcceptedRows {
  const previousRows = input.previous?.tables[table.id]?.extraRows ?? [];
  const incomingRows = input.incoming.tables[table.id]?.extraRows;
  const columns = tableColumns(table, access, ruleLocked);
  const reopenedRows = new Set(input.options?.reopenedExtraRows?.[table.id] ?? []);
  const reopened = (rowIndex: number): boolean =>
    input.options?.reopenHistoricalRows === true || reopenedRows.has(rowIndex);
  const rows: ExtraRow[] = previousRows.map((row, rowIndex) =>
    reopened(rowIndex) ? correctedRow(row, incomingRows?.[rowIndex], columns.writable) : { ...row },
  );
  if (!incomingRows) return { rows, issues: [] };

  const modelColumns = modelRowFieldByColumn(table);
  const historicalIssues = previousRows.flatMap((row, rowIndex) =>
    refusedHistoricalCells(row, incomingRows[rowIndex], rowIndex, table, modelColumns, {
      reopened: reopened(rowIndex),
      editable: columns.editable,
    }),
  );
  const appended = appendedRows(table, incomingRows.slice(previousRows.length), previousRows.length, columns);
  return { rows: [...rows, ...appended.rows], issues: [...historicalIssues, ...appended.issues] };
}

function acceptTables(
  input: AcceptAnswersInput,
  access: FieldAccessResolver,
  ruleLocked: ReadonlySet<string>,
): AcceptedTables {
  const tables: Record<string, TableAnswers> = {};
  const issues: ValidationIssue[] = [];
  for (const table of multipleTables(input.structure)) {
    const accepted = acceptTable(input, table, access, ruleLocked);
    issues.push(...accepted.issues);
    if (accepted.rows.length > 0) tables[table.id] = { extraRows: accepted.rows };
  }
  return { tables, issues };
}

export interface ExpandedAnswers {
  answers: MatrixAnswers;
  issues: ValidationIssue[];
}

// The body names only the rows it adds and the historical cells it changes, so it stays within the
// size cap however long the history grows. Each historical row becomes the sparse row of its edits,
// in front of the new rows, which is the numbering acceptTables reads.
export function withCorrectedRows(previous: MatrixAnswers | null, incoming: AnswersBody): ExpandedAnswers {
  const tables: Record<string, TableAnswers> = {};
  const issues: ValidationIssue[] = [];

  for (const [tableId, entry] of Object.entries(incoming.tables)) {
    const historical = previous?.tables[tableId]?.extraRows ?? [];
    const rows: ExtraRow[] = historical.map(() => ({}));
    for (const [rowKey, cells] of Object.entries(entry.editedRows ?? {})) {
      const rowIndex = /^\d+$/.test(rowKey) ? Number(rowKey) : Number.NaN;
      if (!Number.isInteger(rowIndex) || rowIndex >= historical.length) {
        issues.push({
          fieldId: tableId,
          code: 'type',
          message: TABLE_EDITED_ROW_MISSING_MESSAGE,
          ...(Number.isInteger(rowIndex) ? { cell: { tableId, rowIndex } } : {}),
        });
        continue;
      }
      rows[rowIndex] = { ...cells };
    }
    tables[tableId] = { extraRows: [...rows, ...entry.extraRows] };
  }

  return { answers: { version: 1, matrixId: incoming.matrixId, values: incoming.values, tables }, issues };
}

function changedValueIssue(
  incoming: Record<string, FieldValue>,
  fieldId: string,
  base: FieldValue | undefined,
  code: 'forbidden' | 'frozen',
): ValidationIssue | null {
  if (!hasStoredValue(incoming, fieldId) || sameAnswer(incoming[fieldId], base)) return null;
  return { fieldId, code, message: code === 'forbidden' ? FORBIDDEN_MESSAGE : FROZEN_MESSAGE };
}

function lockedFieldIssue(
  entry: FieldEntry,
  lock: { editable: boolean; isFrozen: boolean },
  previous: Record<string, FieldValue>,
  incoming: Record<string, FieldValue>,
): ValidationIssue | null {
  const fieldId = entry.element.id;
  if (!lock.editable) {
    return changedValueIssue(incoming, fieldId, canonicalBase(entry, shownValue(entry, previous)), 'forbidden');
  }
  if (lock.isFrozen) {
    return changedValueIssue(incoming, fieldId, canonicalBase(entry, storedValue(previous, fieldId)), 'frozen');
  }
  return null;
}

function uniqueFieldEntries(structure: FormStructure): FieldEntry[] {
  const seen = new Set<string>();
  return fieldEntries(migratedElements(structure)).filter(({ element }) => {
    if (seen.has(element.id)) return false;
    seen.add(element.id);
    return true;
  });
}

// A key the source does not have stays out of the result instead of becoming the empty value:
// compileMatrix applies the builder default to a missing key, and an answer the person cleared
// ('') must stay distinguishable from a field nobody touched.
function acceptedValues(
  input: AcceptAnswersInput,
  entries: FieldEntry[],
  writes: (fieldId: string) => boolean,
): Record<string, FieldValue> {
  const previous = input.previous?.values ?? {};
  const values: Record<string, FieldValue> = {};
  for (const { element } of entries) {
    const source = writes(element.id) ? input.incoming.values : previous;
    if (hasStoredValue(source, element.id)) values[element.id] = source[element.id];
  }
  return values;
}

// What a rule hides or disables keeps the saved value, silently: the sheet sends hidden fields on
// purpose, since a hidden field can still trigger another rule, and a person may type in a field
// before a rule hides it.
export function buildAcceptedAnswers(input: AcceptAnswersInput): AcceptedAnswers {
  const access = resolveFieldAccess(input.permissions, input.subject, input.options?.defaultAccess);
  const frozen = new Set(input.options?.frozenFieldIds ?? []);
  const entries = uniqueFieldEntries(input.structure);
  const previous = input.previous?.values ?? {};
  const permitted = (fieldId: string): boolean => access(fieldId).editable && !frozen.has(fieldId);

  const ruleLocked = ruleLockedIds(input.structure, input.rules, input.incoming, (locked) =>
    acceptedValues(input, entries, (fieldId) => permitted(fieldId) && !locked.has(fieldId)),
  );
  const values = acceptedValues(input, entries, (fieldId) => permitted(fieldId) && !ruleLocked.has(fieldId));
  const issues = entries.flatMap((entry) => {
    const lock = { editable: access(entry.element.id).editable, isFrozen: frozen.has(entry.element.id) };
    return lockedFieldIssue(entry, lock, previous, input.incoming.values) ?? [];
  });
  const tables = acceptTables(input, access, ruleLocked);

  return {
    answers: { version: 1, matrixId: input.incoming.matrixId, values, tables: tables.tables },
    issues: [...issues, ...tables.issues],
  };
}
